import React from "react";
import OnboardingScreen, { OnboardingTitle } from "../OnboardingScreen";
import { OnboardingNextButton } from "../OnboardingButtons";
import { OnboardingRadioRow } from "../OnboardingRows";
import PeakTextField from "../PeakTextField";
import { DOB_MIN, OnboardingSex, SEX_OPTIONS } from "../onboardingCopy";
import { usernameSyntaxError } from "../onboardingValidation";
import type { UsernameStatus } from "../../../hooks/useOnboardingWizard";

interface PersonalInfoStepProps {
  firstName: string;
  username: string;
  usernameStatus: UsernameStatus;
  sex: OnboardingSex | null;
  dob: string;
  progress: number | null;
  canContinue: boolean;
  onFirstNameChange: (value: string) => void;
  onUsernameChange: (value: string) => void;
  onSexChange: (value: OnboardingSex) => void;
  onDobChange: (value: string) => void;
  onBack: () => void;
  onContinue: () => void;
}

const todayISO = () => new Date().toISOString().slice(0, 10);

const PersonalInfoStep: React.FC<PersonalInfoStepProps> = ({
  firstName,
  username,
  usernameStatus,
  sex,
  dob,
  progress,
  canContinue,
  onFirstNameChange,
  onUsernameChange,
  onSexChange,
  onDobChange,
  onBack,
  onContinue,
}) => {
  const syntaxHint = username ? usernameSyntaxError(username) : null;

  const trailing = (() => {
    if (!username || syntaxHint) return null;
    switch (usernameStatus.kind) {
      case "checking": return <span className="text-peak-text-tertiary">Checking…</span>;
      case "available": return <span className="text-peak-success">Available</span>;
      case "taken":
      case "error": return <span className="text-peak-error">Unavailable</span>;
      default: return null;
    }
  })();

  return (
    <OnboardingScreen
      progress={progress}
      onBack={onBack}
      scroll
      footer={<OnboardingNextButton label="Continue" enabled={canContinue} onClick={onContinue} />}
    >
      <form
        className="flex flex-col gap-7"
        onSubmit={(e) => {
          e.preventDefault();
          if (canContinue) onContinue();
        }}
      >
        <OnboardingTitle title="Personal Information" subtitle="Personalise your experience." />
        <PeakTextField
          label="First name"
          name="given-name"
          value={firstName}
          onChange={onFirstNameChange}
          placeholder="Your first name"
          autoComplete="given-name"
          autoCapitalize="words"
        />
        <div className="flex flex-col gap-1.5">
          <PeakTextField
            label="Username"
            name="username"
            value={username}
            onChange={onUsernameChange}
            placeholder="Pick a unique username"
            autoComplete="username"
            autoCapitalize="none"
            trailing={trailing}
            error={Boolean(syntaxHint) || usernameStatus.kind === "taken"}
          />
          {syntaxHint && <p className="font-funnel text-[12px] font-medium text-peak-error">{syntaxHint}</p>}
          {!syntaxHint && usernameStatus.kind === "taken" && (
            <p className="font-funnel text-[12px] font-medium text-peak-error">That username is taken.</p>
          )}
          {!syntaxHint && usernameStatus.kind === "error" && (
            <p className="font-funnel text-[12px] font-medium text-peak-error">{usernameStatus.message}</p>
          )}
        </div>
        <div className="flex flex-col gap-3" role="radiogroup" aria-label="Sex">
          {SEX_OPTIONS.map((option) => (
            <OnboardingRadioRow key={option.wire} title={option.label} selected={sex === option.wire} onClick={() => onSexChange(option.wire)} />
          ))}
        </div>
        <label className="flex flex-col gap-2">
          <span className="font-funnel text-[11px] font-medium text-peak-text-tertiary">Date of Birth</span>
          <input
            type="date"
            value={dob}
            min={DOB_MIN}
            max={todayISO()}
            onChange={(e) => e.target.value && onDobChange(e.target.value)}
            className="min-h-[60px] w-full rounded-lg border border-peak-border bg-peak-bg-secondary px-4 font-funnel text-[15px] font-semibold text-peak-text outline-none focus:border-peak-text"
            required
          />
        </label>
      </form>
    </OnboardingScreen>
  );
};

export default PersonalInfoStep;
